<template>
  <div class="user-accreditation-manager">
    <button class="btn btn-primary" @click="showModal = true">
      Manage Accreditations
    </button>

    <Modal v-model="showModal" title="Manage User Accreditations" size="lg">
      <div v-if="isLoading" class="text-center">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
      <div v-else-if="error" class="alert alert-danger">
        {{ error }}
      </div>
      <div v-else>
        <!-- Organization Search -->
        <div class="row mb-4">
          <div class="col-sm-12">
            <h4>Add New Accreditation</h4>
            <div class="search-input">
              <input
                type="search"
                class="form-control"
                placeholder="Search organizations..."
                v-model="searchText"
                aria-label="Search organizations"
              />
            </div>
            <div class="list-container mt-3">
              <h4 class="text-center text-muted" v-if="!searchResults || searchResults.length === 0">
                {{ searchText ? 'No matching organizations found.' : 'No organizations available for accreditation.' }}
              </h4>
              <div
                v-for="org in searchResults"
                :key="org.id"
                class="organization-list-item"
                :class="{ selected: org === selectedOrganization }"
                @click="selectOrganization(org)"
              >
                <span class="name">{{ org.name }}</span>
                <div class="mt-2">
                  <button 
                    class="btn btn-sm btn-primary me-2"
                    @click.stop="accreditOrganization(org, 'Accredited')"
                  >
                    Accredit
                  </button>
                  <button 
                    class="btn btn-sm btn-success"
                    @click.stop="accreditOrganization(org, 'SuperAccredited')"
                  >
                    Super Accredit
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Existing Accreditations -->
        <div class="row">
          <div class="col-sm-12">
            <h4>Current Accreditations</h4>
            <div v-if="accreditations.length === 0" class="text-muted text-center">
              No accreditations found for this user
            </div>
            <div v-else class="list-group">
              <div v-for="accreditation in accreditations" :key="accreditation.id" class="list-group-item">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 class="mb-1">{{ accreditation.organisationName }}</h5>
                    <p class="mb-1">
                      <span class="badge" :class="accreditation.role === 'SuperAccredited' ? 'bg-success' : 'bg-primary'">
                        {{ accreditation.role }}
                      </span>
                      <small class="text-muted ms-2">
                        Granted: {{ formatDate(accreditation.grantedDate) }}
                      </small>
                    </p>
                    <p v-if="accreditation.notes" class="mb-1 text-muted">
                      <small>{{ accreditation.notes }}</small>
                    </p>
                  </div>
                  <div>
                    <button 
                      class="btn btn-sm btn-outline-primary me-2"
                      @click="updateAccreditation(accreditation)"
                    >
                      {{ accreditation.role === 'Accredited' ? 'Change to SuperAccredited' : 'Change to Accredited' }}
                    </button>
                    <button 
                      class="btn btn-sm btn-outline-danger"
                      @click="removeAccreditation(accreditation)"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import axios from 'axios';
import { Modal } from 'uiv';

interface OrganisationListItemApiModel {
  id: string;
  name: string;
}

interface AccreditationApiModel {
  id: number;
  organisationId: string;
  organisationName: string;
  role: string;
  grantedDate: string;
  notes?: string;
}

defineOptions({ name: 'UserAccreditationManager' });

const props = defineProps<{
  userId: string;
}>();

const showModal = ref(false);
const isLoading = ref(false);
const isSearching = ref(false);
const error = ref<string | null>(null);
const accreditations = ref<AccreditationApiModel[]>([]);
const searchText = ref('');
const allOrganisations = ref<OrganisationListItemApiModel[]>([]);
const searchResults = ref<OrganisationListItemApiModel[] | null>(null);
const selectedOrganization = ref<OrganisationListItemApiModel | null>(null);
const serverCallTimeout = ref<any>(null);

const loadOrganisations = async () => {
  try {
    const response = await axios.get('/api/admin/organisations');
    allOrganisations.value = response.data.map((org: any) => ({
      id: org.id,
      name: org.name
    }));
    performSearch(); // Initial search with empty query to show all available orgs
  } catch (err) {
    error.value = 'Failed to load organizations';
    console.error('Error loading organizations:', err);
  }
};

const loadAccreditations = async () => {
  try {
    isLoading.value = true;
    error.value = null;
    const response = await axios.get(`/api/admin/useraccreditations/${props.userId}`);
    accreditations.value = response.data.map((acc: any) => ({
      id: acc.id,
      organisationId: acc.organisationId,
      organisationName: acc.organisationName,
      role: acc.role,
      grantedDate: acc.grantedDate,
      notes: acc.notes
    }));
  } catch (err) {
    error.value = 'Failed to load accreditations';
    console.error('Error loading accreditations:', err);
  } finally {
    isLoading.value = false;
  }
};

const performSearch = () => {
  if (!allOrganisations.value.length) {
    searchResults.value = null;
    return;
  }

  const query = searchText.value.trim().toLowerCase();
  const existingOrgIds = accreditations.value.map(a => a.organisationId);
  
  // Filter organizations based on search text and exclude already accredited ones
  searchResults.value = allOrganisations.value.filter(
    org => !existingOrgIds.includes(org.id) && 
    (query === '' || org.name.toLowerCase().includes(query))
  );
};

const selectOrganization = (org: OrganisationListItemApiModel) => {
  selectedOrganization.value = org;
};

const accreditOrganization = async (org: OrganisationListItemApiModel, role: string) => {
  try {
    await axios.post('/api/admin/useraccreditation', {
      userId: props.userId,
      organisationId: org.id,
      accreditationRole: role,
      notes: ''
    });
    await loadAccreditations();
    performSearch(); // Refresh the available organizations list
  } catch (err) {
    error.value = 'Failed to add accreditation';
    console.error('Error adding accreditation:', err);
  }
};

const updateAccreditation = async (accreditation: AccreditationApiModel) => {
  const newRole = accreditation.role === 'Accredited' ? 'SuperAccredited' : 'Accredited';
  try {
    await axios.post('/api/admin/useraccreditation', {
      userId: props.userId,
      organisationId: accreditation.organisationId,
      accreditationRole: newRole,
      notes: accreditation.notes || ''
    });
    await loadAccreditations();
  } catch (err) {
    error.value = 'Failed to update accreditation';
    console.error('Error updating accreditation:', err);
  }
};

const removeAccreditation = async (accreditation: AccreditationApiModel) => {
  if (!confirm('Are you sure you want to remove this accreditation?')) {
    return;
  }

  try {
    await axios.post('/api/admin/useraccreditation', {
      userId: props.userId,
      organisationId: accreditation.organisationId
    });
    await loadAccreditations();
    performSearch(); // Refresh the available organizations list
  } catch (err) {
    error.value = 'Failed to remove accreditation';
    console.error('Error removing accreditation:', err);
  }
};

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString();
};

watch(searchText, () => {
  performSearch();
});

watch(showModal, (newValue) => {
  if (newValue) {
    loadAccreditations();
    loadOrganisations();
  }
});

onMounted(() => {
  loadAccreditations();
  loadOrganisations();
});
</script>

<style scoped>
.user-accreditation-manager {
  display: inline-block;
}

.list-container {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 10px;
}

.organization-list-item {
  padding: 10px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
}

.organization-list-item:last-child {
  border-bottom: none;
}

.organization-list-item:hover {
  background-color: #f8f9fa;
}

.organization-list-item.selected {
  background-color: #e9ecef;
}

.search-input {
  margin-bottom: 15px;
}

.list-group-item {
  margin-bottom: 10px;
}

.badge {
  font-size: 0.9em;
  padding: 0.5em 0.8em;
}
</style> 